import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InvoiceService } from 'src/app/features/invoices/services/invoice.service';
import { getTodayDateOnly } from 'src/app/shared/util/date.util';

@Component({
  selector: 'app-finalize-investments-dialog',
  templateUrl: './finalize-investments-dialog.component.html',
  styleUrls: ['./finalize-investments-dialog.component.scss'],
})
export class FinalizeInvestmentsDialogComponent implements OnInit {
  form: FormGroup;
  investments = [];
  loadingInvestments = false;
  selectedInvestmentIds: string[] = [];
  selectAll = false;
  availableToInvestAmount = 0;

  constructor(
    private invoiceService: InvoiceService,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<FinalizeInvestmentsDialogComponent>
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      finalizedDate: [getTodayDateOnly(), Validators.required],
    });
    this.loadInvestments();
  }

  loadInvestments() {
    const invoiceId = this.data?.invoice?._id;
    if (!invoiceId) {
      this.investments = [];
      return;
    }

    this.loadingInvestments = true;
    this.invoiceService.getInvestments(invoiceId).subscribe(
      (response: any) => {
        this.investments = this.sortInvestmentsAlphabetically(
          Array.isArray(response) ? response : []
        );
        this.recalculateAvailableAmount();
        this.loadingInvestments = false;
      },
      () => {
        this.investments = [];
        this.selectedInvestmentIds = [];
        this.selectAll = false;
        this.recalculateAvailableAmount();
        this.loadingInvestments = false;
      }
    );
  }

  sortInvestmentsAlphabetically(investments: any[]): any[] {
    return investments.slice().sort((a, b) =>
      this.getInvestorName(a).localeCompare(this.getInvestorName(b), 'es', {
        sensitivity: 'base',
      })
    );
  }

  getInvestorName(investment: any): string {
    const user = investment?.user;
    const names = user?.names ?? investment?.names;
    const familyNames = user?.familyNames ?? investment?.familyNames;
    return `${names || ''} ${familyNames || ''}`.trim();
  }

  onSelectAllChanged(checked: boolean) {
    this.selectAll = checked;
    if (this.selectAll) {
      this.selectedInvestmentIds = this.investments
        .map((investment: any) => investment?._id)
        .filter((id: string) => !!id);
    } else {
      this.selectedInvestmentIds = [];
    }
    this.recalculateAvailableAmount();
  }

  onToggleInvestment(investmentId: string, checked: boolean) {
    if (!investmentId) return;

    if (checked) {
      if (!this.selectedInvestmentIds.includes(investmentId)) {
        this.selectedInvestmentIds = this.selectedInvestmentIds.concat(investmentId);
      }
    } else {
      this.selectedInvestmentIds = this.selectedInvestmentIds.filter(
        (selectedId) => selectedId !== investmentId
      );
    }

    this.selectAll =
      this.getSelectableInvestmentIds().length > 0 &&
      this.selectedInvestmentIds.length === this.getSelectableInvestmentIds().length;
    this.recalculateAvailableAmount();
  }

  isInvestmentSelected(investmentId: string): boolean {
    return this.selectedInvestmentIds.includes(investmentId);
  }

  getSelectableInvestmentIds(): string[] {
    return this.investments
      .map((investment: any) => investment?._id)
      .filter((id: string) => !!id);
  }

  getSelectedInvestments(): any[] {
    return this.investments.filter((investment: any) =>
      this.selectedInvestmentIds.includes(investment?._id)
    );
  }

  getInvestmentAmount(investment: any): number {
    const rawAmount = investment?.amount ?? 0;
    return this.toNumber(rawAmount);
  }

  recalculateAvailableAmount() {
    const selectedWithdrawAmount = this.getSelectedInvestments().reduce(
      (acc, investment: any) => acc + this.getInvestmentAmount(investment),
      0
    );

    const totalToInvest = this.toNumber(this.data?.invoice?.advanceAmount);
    const currentAvailable = this.toNumber(this.data?.invoice?.availableBalanceAmount);

    // Available after withdraw = current available + withdrawn amount,
    // but bounded by total amount to invest.

    // suma -> currentAvailable + selectedWithdrawAmount
    const rawAvailable = currentAvailable + selectedWithdrawAmount;
    this.availableToInvestAmount = rawAvailable?.toFixed(2);
  }

  toNumber(value: any): number {
    const normalized = Number(
      String(value ?? 0)
        .replace(/\s/g, '')
        .replace(/,/g, '')
    );

    return Number.isNaN(normalized) ? 0 : normalized;
  }

  toCheckHandoff() {
    const finalizedDate = this.form?.get('finalizedDate')?.value;
    if (!finalizedDate || this.selectedInvestmentIds.length === 0) return;

    this.dialogRef.close({
      openConfirmation: true,
      payload: {
        invoice: this.data?.invoice,
        finalizedDate,
        selectedInvestmentIds: this.selectedInvestmentIds,
        selectedInvestments: this.getSelectedInvestments(),
        availableToInvestAmount: this.availableToInvestAmount,
      },
    });
  }

  close() {
    this.dialogRef.close();
  }
}
